import axiosInstance from '../../../core/services/axiosConfig';
import { apis } from '../../../core/services/params';
import React, { useState, useEffect } from "react";
import {
    Table,
    Button,
    Card,
    Badge,
    Typography,
    Tooltip,
    Empty,
    Tag,
    Input,
    Select,
    Space,
    DatePicker,
} from "antd";
import type { TablePaginationConfig } from "antd";
import type { Dayjs } from "dayjs";
import {
    ReloadOutlined,
    ShoppingCartOutlined,
    SearchOutlined,
    ClearOutlined,
    MessageOutlined,
    UserOutlined,
    FilterOutlined,
    EyeOutlined,
    CalendarOutlined,
    ThunderboltOutlined,
    BarChartOutlined,
    ShoppingOutlined,
    RocketOutlined,
} from "@ant-design/icons";
import moment from "moment";

import SideNavbar from "../../components/Navbar/SideNavbar";
import Header from "../../components/Header/Header";
import RfqCareSessionDetails from "../../components/Crm/RfqCareSessionDetails";
import RfqCareSimulator from "../../components/Crm/RfqCareSimulator";
import WhatsAppBotSimulator from "../../components/WhatsAppBotSimulator";

const { Title, Text } = Typography;

// Map current FSM states to display badge colors
const STATE_COLORS: Record<string, string> = {
    INITIAL: "default",
    SUGGEST_ALTERNATIVE: "processing",
    SHOW_MORE_ALTERNATIVES: "geekblue",
    SHOW_BUY_OPTIONS: "purple",
    STOCK_ALERT: "orange",
    WAITING_FOR_STOCK: "gold",
    ASK_QUANTITY: "cyan",
    ASK_BULK_BUDGET: "volcano",
    ASK_ADDRESS: "lime",
    CONFIRM_ADDRESS: "success",
};

// All known FSM states for the current_state dropdown filter
const ALL_STATES = [
    "INITIAL",
    "SUGGEST_ALTERNATIVE",
    "SHOW_MORE_ALTERNATIVES",
    "SHOW_BUY_OPTIONS",
    "STOCK_ALERT",
    "WAITING_FOR_STOCK",
    "ASK_QUANTITY",
    "ASK_BULK_BUDGET",
    "ASK_ADDRESS",
    "CONFIRM_ADDRESS",
];

// Filter shape used for both input state and committed state
interface RfqFilters {
    user_id: string;
    current_state: string;
    start_date: string;
    end_date: string;
    is_order_attempted: string;
}

const QUICK_FILTERS = [
    { label: "Today", key: "today" },
    { label: "Yesterday", key: "yesterday" },
    { label: "Last 3 Days", key: "last_3_days" },
    { label: "Last 7 Days", key: "last_7_days" },
];

const EMPTY_FILTERS: RfqFilters = { user_id: "", current_state: "", start_date: "", end_date: "", is_order_attempted: "" };

// Analytics data shape returned from the API
interface RfqAnalytics {
    group_by_state_stats: Record<string, number>;
    order_attempted_stats: number;
}

// Main RFQ Care Sessions listing page
const RfqCareSessions = () => {
    const [sessions, setSessions] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ page: 1, page_size: 10, total: 0 });
    // Analytics state populated from each API response
    const [analytics, setAnalytics] = useState<RfqAnalytics | null>(null);

    // Separate input state (what user types) from committed filters (applied to fetch)
    const [inputFilters, setInputFilters] = useState<RfqFilters>(EMPTY_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState<RfqFilters>(EMPTY_FILTERS);

    // Selected session for the detail modal
    const [selectedSession, setSelectedSession] = useState<any>(null);
    const [detailsVisible, setDetailsVisible] = useState(false);

    // RFQ Care Simulator visibility
    const [simulatorVisible, setSimulatorVisible] = useState(false);
    // WhatsApp Bot Simulator visibility
    const [waBotSimVisible, setWaBotSimVisible] = useState(false);

    const handleRowClick = (session: any) => {
        setSelectedSession(session);
        setDetailsVisible(true);
    };

    const handleCloseDetails = () => {
        setDetailsVisible(false);
        setSelectedSession(null);
    };

    // Build query string and fetch paginated sessions from the RFQ Care API
    const fetchSessions = async (
        page = 1,
        page_size = 10,
        currentFilters: RfqFilters = EMPTY_FILTERS
    ) => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append("page", String(page));
            params.append("page_size", String(page_size));
            if (currentFilters.user_id) params.append("user_id", currentFilters.user_id);
            if (currentFilters.current_state) params.append("current_state", currentFilters.current_state);
            if (currentFilters.start_date) params.append("start_date", currentFilters.start_date);
            if (currentFilters.end_date) params.append("end_date", currentFilters.end_date);
            // Append is_order_attempted only when explicitly set to true or false
            if (currentFilters.is_order_attempted !== "") params.append("is_order_attempted", currentFilters.is_order_attempted);

            const response = await axiosInstance.get(
                `${apis.RFQ_CARE_USER_SESSIONS}?${params.toString()}`
            );
            const result = response.data;
            if (result.success && result.data) {
                setSessions(result.data.sessions || []);
                setPagination({
                    page: result.data.page,
                    page_size: result.data.page_size,
                    total: result.data.total,
                });
                // Capture analytics from API response for display above the table
                if (result.analytics) {
                    setAnalytics(result.analytics as RfqAnalytics);
                }
            }
        } catch (error) {
            console.error("Error fetching RFQ Care sessions:", error);
        } finally {
            setLoading(false);
        }
    };

    // Apply a quick date preset and immediately fetch results
    const handleQuickFilter = (key: string) => {
        const today = moment().format("YYYY-MM-DD");
        let start = today;
        let end = today;
        if (key === "yesterday") {
            start = moment().subtract(1, "days").format("YYYY-MM-DD");
            end = moment().subtract(1, "days").format("YYYY-MM-DD");
        } else if (key === "last_3_days") {
            start = moment().subtract(2, "days").format("YYYY-MM-DD");
        } else if (key === "last_7_days") {
            start = moment().subtract(6, "days").format("YYYY-MM-DD");
        }
        const updated = { ...inputFilters, start_date: start, end_date: end };
        setInputFilters(updated);
        setAppliedFilters(updated);
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchSessions(1, pagination.page_size, updated);
    };

    useEffect(() => {
        fetchSessions(1, 10, EMPTY_FILTERS);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleTableChange = (pag: TablePaginationConfig) => {
        fetchSessions(pag.current, pag.pageSize, appliedFilters);
    };

    const handleApplyFilters = () => {
        // Commit input filters and re-fetch from page 1
        setAppliedFilters(inputFilters);
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchSessions(1, pagination.page_size, inputFilters);
    };

    const handleResetFilters = () => {
        // Clear all filters and re-fetch with empty params
        setInputFilters(EMPTY_FILTERS);
        setAppliedFilters(EMPTY_FILTERS);
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchSessions(1, pagination.page_size, EMPTY_FILTERS);
    };

    // Check if any filter is currently active to show the active indicator
    const hasActiveFilters =
        appliedFilters.user_id !== "" ||
        appliedFilters.current_state !== "" ||
        appliedFilters.start_date !== "" ||
        appliedFilters.end_date !== "";

    // Table columns definition for RFQ Care sessions listing
    const columns = [
        {
            title: "User ID",
            dataIndex: "user_id",
            key: "user_id",
            width: 140,
            render: (text: string, record: any) => (
                // Render User ID as a visible blue link to indicate clickability
                <span
                    onClick={() => handleRowClick(record)}
                    className="font-mono text-xs text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium"
                >
                    {text}
                </span>
            ),
        },
        {
            title: "User",
            key: "user_name",
            width: 160,
            render: (_: any, record: any) => {
                const userInfo = record.context?.user_info || {};
                return (
                    <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-800">{userInfo.first_name || "—"}</span>
                        <span className="text-xs text-gray-400 font-mono">{userInfo.mobile || record.user_id}</span>
                    </div>
                );
            },
        },
        {
            title: "Product Enquired",
            key: "product",
            render: (_: any, record: any) => {
                const productInfo = record.context?.rfq_product_info || {};
                return (
                    <Tooltip title={productInfo.product_name}>
                        <div className="flex flex-col max-w-[260px]">
                            <span className="text-sm text-gray-800 truncate">{productInfo.product_name || "—"}</span>
                            <span className="text-[10px] text-gray-400 font-mono">{productInfo.product_id}</span>
                        </div>
                    </Tooltip>
                );
            },
        },
        {
            title: "State",
            dataIndex: "current_state",
            key: "current_state",
            width: 180,
            render: (state: string) => (
                <Tag color={STATE_COLORS[state] || "default"} className="text-xs">
                    {state}
                </Tag>
            ),
        },
        {
            title: "Messages",
            dataIndex: "chat_history",
            key: "messages",
            width: 100,
            render: (history: any[]) => (
                <Tooltip title={`${history?.length || 0} total messages`}>
                    <div className="flex items-center gap-1.5">
                        <MessageOutlined className="text-gray-400 text-xs" />
                        <Badge
                            count={history?.length || 0}
                            showZero
                            style={{ backgroundColor: '#6366f1' }}
                            overflowCount={99}
                        />
                    </div>
                </Tooltip>
            ),
        },
        {
            title: "Stock Status",
            key: "stock_status",
            width: 120,
            render: (_: any, record: any) => {
                const status = record.context?.rfq_product_status || {};
                if (status.in_stock === true) return <Badge status="success" text={<span className="text-xs text-green-600">In Stock</span>} />;
                if (Object.keys(status).length === 0) return <Badge status="error" text={<span className="text-xs text-red-400">Out of Stock</span>} />;
                return <span className="text-xs text-gray-400">—</span>;
            },
        },
        {
            title: "Last Activity",
            dataIndex: "last_activity",
            key: "last_activity",
            width: 140,
            render: (text: string) => (
                <div className="flex flex-col">
                    <span className="text-gray-700 text-xs font-medium">{moment.parseZone(text).format("MMM DD, YYYY")}</span>
                    <span className="text-gray-400 text-[10px]">{moment.parseZone(text).format("HH:mm:ss")}</span>
                </div>
            ),
        },
        {
            title: "Created At",
            dataIndex: "created_at",
            key: "created_at",
            width: 140,
            render: (text: string) => (
                <div className="flex flex-col">
                    <span className="text-gray-700 text-xs font-medium">{moment.parseZone(text).format("MMM DD, YYYY")}</span>
                    <span className="text-gray-400 text-[10px]">{moment.parseZone(text).format("HH:mm:ss")}</span>
                </div>
            ),
        },
        {
            // Actions column with explicit View Details button for row navigation
            title: "",
            key: "actions",
            width: 120,
            fixed: "right" as const,
            render: (_: any, record: any) => (
                <Button
                    type="primary"
                    ghost
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => handleRowClick(record)}
                    className="text-xs"
                >
                    View Details
                </Button>
            ),
        },
    ];

    // Compute total sessions across all states for percentage calculation
    const totalStateSessions = analytics
        ? Object.values(analytics.group_by_state_stats).reduce((a, b) => a + b, 0)
        : 0;

    return (
        <>
            <div className="flex mainWrapper w-full">
                <div className="sidenav-wrapper">
                    <SideNavbar />
                </div>
                <div className="right-container">
                    <div className="header-container">
                        <Header />
                    </div>
                    <div className="body-container">
                        <div className="space-y-6 max-w-[1600px] mx-auto p-6">
                            {/* Page header with title and refresh button */}
                            <div className="flex justify-between items-center bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600 text-2xl">
                                        <ShoppingCartOutlined />
                                    </div>
                                    <div>
                                        <Title level={3} style={{ marginBottom: 0, letterSpacing: '-0.5px' }}>RFQ Care Sessions</Title>
                                        <Text type="secondary">User interactions via the RFQ Care chatbot</Text>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    {hasActiveFilters && (
                                        <Tag color="blue" icon={<FilterOutlined />} className="text-xs">
                                            Filters Active
                                        </Tag>
                                    )}
                                    <Button
                                        type="primary"
                                        icon={<RocketOutlined />}
                                        onClick={() => setWaBotSimVisible(true)}
                                        style={{ background: '#5B5BD6', borderColor: '#5B5BD6' }}
                                    >
                                        🤖 Online Bot
                                    </Button>
                                    <Button
                                        type="primary"
                                        icon={<RocketOutlined />}
                                        onClick={() => setSimulatorVisible(true)}
                                        style={{ background: '#25D366', borderColor: '#25D366' }}
                                    >
                                        Launch Simulator
                                    </Button>
                                    <Button
                                        icon={<ReloadOutlined />}
                                        onClick={() => fetchSessions(1, pagination.page_size, appliedFilters)}
                                        loading={loading}
                                    >
                                        Refresh
                                    </Button>
                                </div>
                            </div>

                            {/* Compact analytics bar – single row with KPI chips and state counts */}
                            {analytics && (
                                <div className="bg-white border border-gray-100 rounded-xl shadow-sm px-4 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-2">
                                    {/* KPI chips group */}
                                    <div className="flex items-center gap-3 flex-shrink-0">
                                        <div className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1">
                                            <ShoppingOutlined className="text-emerald-600 text-xs" />
                                            <span className="text-[11px] text-emerald-700 font-medium">Orders:</span>
                                            <span className="text-[13px] font-bold text-emerald-700">{analytics.order_attempted_stats}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded-lg px-2.5 py-1">
                                            <BarChartOutlined className="text-blue-600 text-xs" />
                                            <span className="text-[11px] text-blue-700 font-medium">Sessions:</span>
                                            <span className="text-[13px] font-bold text-blue-700">{totalStateSessions}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5 bg-violet-50 border border-violet-200 rounded-lg px-2.5 py-1">
                                            <ShoppingCartOutlined className="text-violet-600 text-xs" />
                                            <span className="text-[11px] text-violet-700 font-medium">CVR:</span>
                                            <span className="text-[13px] font-bold text-violet-700">
                                                {totalStateSessions > 0
                                                    ? `${((analytics.order_attempted_stats / totalStateSessions) * 100).toFixed(1)}%`
                                                    : '—'}
                                            </span>
                                        </div>
                                    </div>
                                    {/* Divider between KPIs and state pills */}
                                    <div className="h-6 w-px bg-gray-200 flex-shrink-0 hidden sm:block" />
                                    {/* State distribution as compact inline pills sorted by count */}
                                    <div className="flex flex-wrap items-center gap-1.5 flex-1 min-w-0">
                                        {Object.entries(analytics.group_by_state_stats)
                                            .sort(([, a], [, b]) => b - a)
                                            .map(([state, count]) => (
                                                // Compact state pill with count badge
                                                <Tooltip key={state} title={`${state}: ${count} sessions`}>
                                                    <span className="inline-flex items-center gap-1 cursor-default">
                                                        <Tag
                                                            color={STATE_COLORS[state] || 'default'}
                                                            style={{ fontSize: 10, lineHeight: '18px', padding: '0 5px', margin: 0 }}
                                                        >
                                                            {state.replace(/_/g, ' ')}
                                                        </Tag>
                                                        <span className="text-[11px] font-semibold text-gray-600 -ml-0.5 mr-0.5">{count}</span>
                                                    </span>
                                                </Tooltip>
                                            ))}
                                    </div>
                                </div>
                            )}

                            {/* Filter bar with user_id, state dropdown, date range pickers, and quick filters */}
                            <Card bordered={false} className="shadow-sm rounded-xl">
                                {/* Quick date range preset buttons row */}
                                <div className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-100">
                                    <ThunderboltOutlined className="text-gray-400 text-xs" />
                                    <span className="text-xs text-gray-500 font-medium mr-1">Quick:</span>
                                    {QUICK_FILTERS.map(qf => (
                                        <Button
                                            key={qf.key}
                                            size="small"
                                            type={
                                                appliedFilters.start_date !== "" &&
                                                    appliedFilters.start_date === (
                                                        qf.key === "today" ? moment().format("YYYY-MM-DD") :
                                                            qf.key === "yesterday" ? moment().subtract(1, "days").format("YYYY-MM-DD") :
                                                                qf.key === "last_3_days" ? moment().subtract(2, "days").format("YYYY-MM-DD") :
                                                                    moment().subtract(6, "days").format("YYYY-MM-DD")
                                                    ) &&
                                                    appliedFilters.end_date === (
                                                        qf.key === "yesterday"
                                                            ? moment().subtract(1, "days").format("YYYY-MM-DD")
                                                            : moment().format("YYYY-MM-DD")
                                                    )
                                                    ? "primary"
                                                    : "default"
                                            }
                                            onClick={() => handleQuickFilter(qf.key)}
                                            className="text-xs rounded-lg"
                                        >
                                            {qf.label}
                                        </Button>
                                    ))}
                                </div>

                                {/* Main filter inputs row */}
                                <div className="flex flex-wrap gap-4 items-end">
                                    <div className="flex flex-col gap-1">
                                        <span className="text-xs text-gray-500 font-medium">User ID</span>
                                        <Input
                                            placeholder="e.g. 8625034040"
                                            prefix={<UserOutlined className="text-gray-400" />}
                                            value={inputFilters.user_id}
                                            onChange={e => setInputFilters(prev => ({ ...prev, user_id: e.target.value }))}
                                            onPressEnter={handleApplyFilters}
                                            style={{ width: 200 }}
                                            allowClear
                                            className="rounded-lg"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <span className="text-xs text-gray-500 font-medium">Current State</span>
                                        <Select
                                            placeholder="Select state"
                                            value={inputFilters.current_state || undefined}
                                            onChange={val => setInputFilters(prev => ({ ...prev, current_state: val || "" }))}
                                            allowClear
                                            style={{ width: 200 }}
                                            options={ALL_STATES.map(s => ({
                                                label: (
                                                    <span>
                                                        <Tag color={STATE_COLORS[s] || "default"} className="text-xs mr-1">{s}</Tag>
                                                    </span>
                                                ),
                                                value: s,
                                            }))}
                                        />
                                    </div>
                                    {/* Order attempted boolean filter dropdown */}
                                    <div className="flex flex-col gap-1">
                                        <span className="text-xs text-gray-500 font-medium flex items-center gap-1">
                                            <ShoppingOutlined className="text-[10px]" /> Order Attempted
                                        </span>
                                        <Select
                                            placeholder="Any"
                                            value={inputFilters.is_order_attempted || undefined}
                                            onChange={val => setInputFilters(prev => ({ ...prev, is_order_attempted: val ?? "" }))}
                                            allowClear
                                            style={{ width: 150 }}
                                            options={[
                                                { label: "Yes (True)", value: "true" },
                                                { label: "No (False)", value: "false" },
                                            ]}
                                        />
                                    </div>
                                    {/* Start date picker for date range filter */}
                                    <div className="flex flex-col gap-1">
                                        <span className="text-xs text-gray-500 font-medium flex items-center gap-1">
                                            <CalendarOutlined className="text-[10px]" /> Start Date
                                        </span>
                                        <DatePicker
                                            placeholder="From date"
                                            style={{ width: 150 }}
                                            format="YYYY-MM-DD"
                                            value={inputFilters.start_date ? (require("dayjs")(inputFilters.start_date) as Dayjs) : null}
                                            onChange={(_: Dayjs | null, dateStr: string | null) =>
                                                setInputFilters(prev => ({ ...prev, start_date: dateStr ?? "" }))
                                            }
                                            allowClear
                                            className="rounded-lg"
                                        />
                                    </div>
                                    {/* End date picker for date range filter */}
                                    <div className="flex flex-col gap-1">
                                        <span className="text-xs text-gray-500 font-medium flex items-center gap-1">
                                            <CalendarOutlined className="text-[10px]" /> End Date
                                        </span>
                                        <DatePicker
                                            placeholder="To date"
                                            style={{ width: 150 }}
                                            format="YYYY-MM-DD"
                                            value={inputFilters.end_date ? (require("dayjs")(inputFilters.end_date) as Dayjs) : null}
                                            onChange={(_: Dayjs | null, dateStr: string | null) =>
                                                setInputFilters(prev => ({ ...prev, end_date: dateStr ?? "" }))
                                            }
                                            allowClear
                                            className="rounded-lg"
                                        />
                                    </div>
                                    <Space>
                                        <Button
                                            type="primary"
                                            icon={<SearchOutlined />}
                                            onClick={handleApplyFilters}
                                            loading={loading}
                                        >
                                            Search
                                        </Button>
                                        <Button
                                            icon={<ClearOutlined />}
                                            onClick={handleResetFilters}
                                        >
                                            Reset
                                        </Button>
                                    </Space>
                                </div>
                            </Card>


                            {/* Paginated sessions data table with row-click to open detail modal */}
                            <Card bordered={false} className="shadow-sm rounded-xl overflow-hidden" styles={{ body: { padding: 0 } }}>
                                <Table
                                    columns={columns as any}
                                    dataSource={sessions}
                                    loading={loading}
                                    rowKey={(record: any) => `${record.user_id}-${record.created_at}`}
                                    pagination={{
                                        current: pagination.page,
                                        pageSize: pagination.page_size,
                                        total: pagination.total,
                                        showSizeChanger: true,
                                        pageSizeOptions: ["10", "20", "50"],
                                        showTotal: (total, range) => (
                                            <span className="text-gray-500 mx-4">{`${range[0]}-${range[1]} of ${total} sessions`}</span>
                                        ),
                                        className: "p-4",
                                    }}
                                    onChange={handleTableChange}
                                    onRow={(record: any) => ({
                                        onClick: () => handleRowClick(record),
                                        style: { cursor: "pointer" },
                                        className: "hover:bg-blue-50 transition-colors",
                                    })}
                                    scroll={{ x: "max-content" }}
                                    size="middle"
                                    locale={{
                                        emptyText: (
                                            <Empty
                                                description="No RFQ Care sessions found"
                                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                            />
                                        ),
                                    }}
                                />
                            </Card>
                        </div>
                    </div>
                </div>
            </div>

            {/* Session detail modal shown on row click */}
            <RfqCareSessionDetails
                open={detailsVisible}
                session={selectedSession}
                onClose={handleCloseDetails}
            />

            {/* RFQ Care WhatsApp Simulator */}
            <RfqCareSimulator
                visible={simulatorVisible}
                onClose={() => setSimulatorVisible(false)}
            />

            {/* Online WhatsApp Bot Simulator (localhost:5005/webhook) */}
            <WhatsAppBotSimulator
                visible={waBotSimVisible}
                onClose={() => setWaBotSimVisible(false)}
            />
        </>
    );
};

export default RfqCareSessions;
